import { createAtlAttributionHeader } from '../../utils';
import { type ProductContextKey } from './lib/realtime';
import { validateRealtimeChannelToken } from './lib/realtimeToken';
import { fg } from '@atlaskit/platform-feature-flags';
import { commitMutation, graphql, useRelayEnvironment } from 'react-relay';
import type { usePublishRealtimeChannelMutation } from './__generated__/usePublishRealtimeChannelMutation.graphql';

export type PublishRealtimeChannelPayload = {
	data: {
		channelName: string;
		eventPayload: string | Record<string, unknown>;
		isGlobal?: boolean;
		options?: {
			contextOverrides?: ProductContextKey[];
			token?: string;
		};
	};
};

type RealtimeEventResult = {
	errors?: string[]
	eventId: string | null;
	eventTimestamp: string | null;
};

export type PublishRealtimeChannel = (
	payload: PublishRealtimeChannelPayload,
	contextToken?: string,
) => Promise<RealtimeEventResult | undefined>;

export const usePublishRealtimeChannel = (
	installationId: string,
	context: string,
	appId?: string,
) => {
	const relayEnvironment = useRelayEnvironment();

	return async (
		payload: PublishRealtimeChannelPayload,
		contextToken?: string,
	): Promise<RealtimeEventResult | undefined> => {
		const { channelName, isGlobal, options } = payload.data;

		const { token, contextOverrides } = options || {};

		const prevalidate = fg('forge-ui-realtime-add-pre-permissions-validation') || fg('forge-ui-realtime-pre-validate-frt-existing-fields');

		if (token && prevalidate) {
			const validation = validateRealtimeChannelToken(token, {
				requiredPermission: 'publish',
				channelName,
			});

			if (!validation.valid) {
				return { eventId: null, eventTimestamp: null, errors: [`Realtime token validation failed: ${validation.error}`] };
			}
		}

		const eventPayload = JSON.stringify(payload.data.eventPayload);
		const channelContext = contextOverrides
			? JSON.stringify({
					context,
					contextOverrides,
				})
			: context;

		return new Promise<RealtimeEventResult | undefined>(
			(resolve, reject) => {
				commitMutation<usePublishRealtimeChannelMutation>(relayEnvironment, {
					mutation: graphql`
						mutation usePublishRealtimeChannelMutation(
							$installationId: ID!
							$name: String!
							$payload: String!
							$context: String
							$isGlobal: Boolean
							$token: String
						) {
							ecosystem {
								publishRealtimeChannel(
									installationId: $installationId
									name: $name
									payload: $payload
									context: $context
									isGlobal: $isGlobal
									token: $token
								) {
									eventId
									eventTimestamp
								}
							}
						}
					`,
					variables: {
						installationId,
						name: channelName,
						payload: eventPayload,
						context: channelContext,
						isGlobal: isGlobal ?? false,
						token,
					},
					cacheConfig: {
						metadata: {
							headers: {
								'atl-attribution': createAtlAttributionHeader(),
								...(contextToken ? { 'x-forge-context-token': contextToken } : {}),
								...(fg('forge-ui-realtime-forward-rate-limit-headers') && {
									...(appId ? { 'x-rate-limit-app-id': appId } : {}),
									...(installationId ? { 'x-rate-limit-installation-id': installationId } : {}),
								}),
							},
						},
					},
					onCompleted: (data, errors) => {
						if (errors) {
							if (fg('forge-ui-realtime-fix-error-handling-publish')) {
								return resolve({ eventId: null, eventTimestamp: null, errors: errors.map(error => error.message) });
							} else {
								reject(errors);
							}

							return;
						}

						if (data?.ecosystem?.publishRealtimeChannel) {
							const { eventId, eventTimestamp } = data.ecosystem.publishRealtimeChannel;
							if (eventId && eventTimestamp) {
								resolve({ eventId, eventTimestamp });
							} else {
								resolve(undefined);
							}
						} else {
							resolve(undefined);
						}
					},
					onError: reject,
				});
			},
		);
	};
};
